import * as React from 'react'
import type { EvaluationCriterion, EvaluationTemplate } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Textarea } from '@/components/ui/textarea'
import { scaleLabelFor, stepClass } from '@/components/scale-legend'
import { useCommitOnUnmount } from '@/hooks/use-unsaved'

/**
 * Scoring one criterion: the row of score buttons and the note under it.
 *
 * Shared by the team evaluation (evaluation-detail.tsx) and the individual
 * self-assessment (self-assessment-detail.tsx), because both score criteria
 * from the same catalog — and a criterion with `choices` (0045) must render
 * its own answers on BOTH, or a yes/no question added to the self-assessment
 * would quietly offer 1–5.
 */

type Scale = Pick<EvaluationTemplate, 'scale_min' | 'scale_max' | 'scale_labels'>

/**
 * The scale's label for a point, but only when it is short enough to print on a
 * 36px button — a unit like "40%", not a word like "Integrated".
 *
 * Five characters is the line: it takes "100%" and every percentage below it,
 * and refuses every maturity label in SWEBOK and Process, which keep their
 * numbered buttons and read their words off the legend.
 */
export function unitLabel(template: Scale, score: number): string | null {
  const label = template.scale_labels[score - template.scale_min]
  return typeof label === 'string' && label.length <= 5 ? label : null
}

/**
 * What a score is called on THIS criterion.
 *
 * A criterion with `choices` (0045) names its own answers — 10 is "Yes", not
 * "100%" — and everything else falls back to the template's scale labels. Used
 * wherever a score is shown in words rather than scored.
 */
export function answerLabel(
  template: Scale,
  criterion: Pick<EvaluationCriterion, 'choices'>,
  score: number | null | undefined,
): string | null {
  if (score == null) return null
  const own = criterion.choices?.find((ch) => ch.score === score)
  return own ? own.label : scaleLabelFor(template, score)
}

/**
 * The buttons. Render inside a flex row; the caller adds whatever it shows
 * beside them (the chosen answer in words, the comparison run's score).
 *
 * A criterion may offer fewer answers than the template's scale (0045) — a
 * yes/no is 0 or 10 on the same axis. Its buttons are labelled and wide,
 * because "No" is the answer and 0 is only the number it is stored as.
 * Otherwise the button carries the scale's own label when that label is a unit
 * rather than a word: "40%" IS the answer on the Team AI Skills scale, and a
 * row of bare 0–10 buttons under a question about a percentage asks the room
 * to do the conversion in their heads.
 */
export function ScoreButtons({
  template,
  criterion,
  value,
  onPick,
}: {
  template: Scale
  criterion: Pick<EvaluationCriterion, 'choices'>
  value: number | null
  onPick: (score: number) => void
}) {
  const scale: number[] = []
  for (let v = template.scale_min; v <= template.scale_max; v++) scale.push(v)
  const options = criterion.choices ?? scale.map((v) => ({ score: v, label: unitLabel(template, v) }))
  return (
    <>
      {options.map((choice) => {
        const v = choice.score
        const selected = value === v
        const name = choice.label ?? template.scale_labels[v - template.scale_min] ?? null
        const labelled = Boolean(choice.label)
        return (
          <button
            key={v}
            type="button"
            onClick={() => onPick(v)}
            // The full name is in the legend; this is the per-button reminder.
            title={name ? `${v} — ${name}` : String(v)}
            className={cn(
              'h-9 rounded-md border text-sm font-medium transition-colors',
              labelled ? 'px-4' : 'w-9 tabular-nums',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              // A selected button takes its own step's color, so the scoring
              // UI and the legend agree at a glance.
              selected
                ? cn(stepClass(v - template.scale_min, scale.length), 'border-transparent')
                : 'border-input hover:bg-accent',
            )}
            style={selected ? { background: 'hsl(var(--scale-bg))', color: 'hsl(var(--scale-ink))' } : undefined}
            aria-pressed={selected}
            aria-label={name ? `${v} — ${name}` : String(v)}
          >
            {labelled ? choice.label : v}
          </button>
        )
      })}
    </>
  )
}

/**
 * Notes save on blur rather than on every keystroke — one upsert per edit
 * instead of one per character, and no debounce timer to leak.
 *
 * And on unmount, because blur is not the only way a field goes away: a browser
 * fires no `blur` for an element removed from the DOM while focused, so leaving
 * the page mid-sentence used to drop it. Same rescue the board's inline editor
 * makes (`board-canvas.tsx`), and the same latch — `committed` keeps the two
 * paths from sending the note twice.
 */
export function NoteField({
  initial,
  onCommit,
  placeholder = 'Why this score? (optional)',
}: {
  initial: string
  onCommit: (notes: string) => void
  placeholder?: string
}) {
  const [value, setValue] = React.useState(initial)
  const committed = React.useRef(initial.trim())

  const commit = () => {
    const next = value.trim()
    if (next === committed.current) return
    committed.current = next
    onCommit(next)
  }
  useCommitOnUnmount(commit)

  return (
    <Textarea
      rows={1}
      value={value}
      placeholder={placeholder}
      className="mt-2 min-h-0 resize-y text-xs"
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
    />
  )
}
